/**
 * Stories service with TanStack Query hooks
 */

import { useQuery, useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import type { Story, PaginatedResponse, StoryUploadUrlResponse } from '../types';
import { useAuth } from '../contexts/AuthContext';
import apiClient from '../lib/api-client';
import axios from 'axios';

/**
 * Fetch story feed with pagination
 */
const fetchStoryFeed = async (cursor?: string): Promise<PaginatedResponse<Story>> => {
  const params: Record<string, string> = { limit: '20' };
  if (cursor) {
    params.cursor = cursor;
  }

  const { data } = await apiClient.get<PaginatedResponse<Story>>('/stories', { params });

  return {
    items: data.items || [],
    nextCursor: data.nextCursor,
    hasMore: data.hasMore || false,
  };
};

/**
 * Fetch single story by ID
 */
const fetchStory = async (storyId: string): Promise<Story> => {
  const { data } = await apiClient.get<Story>(`/stories/${storyId}`);
  return data;
};

/**
 * Fetch stories by user ID
 */
const fetchUserStories = async (userId: string): Promise<Story[]> => {
  const { data } = await apiClient.get<{ items: Story[] }>(`/users/${userId}/stories`);
  return data.items || [];
};

/**
 * Helper to get video duration
 */
const getVideoDuration = (file: File): Promise<number> => {
  return new Promise((resolve, reject) => {
    const video = document.createElement('video');
    video.preload = 'metadata';

    const cleanup = () => {
      if (video.src) {
        URL.revokeObjectURL(video.src);
      }
    };

    const timeout = setTimeout(() => {
      cleanup();
      reject(new Error('Timeout loading video metadata'));
    }, 10000);

    video.onloadedmetadata = () => {
      clearTimeout(timeout);
      const duration = video.duration;
      cleanup();

      if (!duration || !isFinite(duration) || duration <= 0) {
        reject(new Error('Video duration could not be determined'));
        return;
      }

      resolve(duration);
    };

    video.onerror = (e) => {
      clearTimeout(timeout);
      cleanup();
      console.error('Video metadata error:', e, 'File:', file.name, 'Type:', file.type);
      reject(new Error(`Failed to load video metadata: ${file.type}`));
    };

    try {
      video.src = URL.createObjectURL(file);
      video.load();
    } catch (error) {
      clearTimeout(timeout);
      cleanup();
      reject(new Error(`Failed to create video URL: ${error}`));
    }
  });
};

/**
 * Upload multiple photos/videos as a story
 */
const uploadStory = async (files: File[], caption?: string): Promise<Story> => {
  if (files.length === 0) {
    throw new Error('At least one file is required');
  }

  if (files.length > 10) {
    throw new Error('Maximum 10 items allowed per story');
  }

  // Get video durations for video files
  const filesWithMetadata = await Promise.all(
    files.map(async (file) => {
      // Check if file is a video by MIME type or extension
      const videoTypes = ['video/mp4', 'video/quicktime', 'video/x-m4v', 'video/webm'];
      const isVideoType = videoTypes.includes(file.type);
      const extension = file.name.split('.').pop()?.toLowerCase() || '';
      const isVideoExt = ['mp4', 'mov', 'm4v', 'webm'].includes(extension);
      const isVideo = isVideoType || isVideoExt;

      let duration: number | undefined;

      if (isVideo) {
        try {
          duration = await getVideoDuration(file);
        } catch (err) {
          // For videos we can't read (like HEVC), estimate based on file size
          // Server will validate actual duration during processing
          console.warn(`Could not read video metadata for ${file.name}, using estimate`);
          duration = 30; // Estimate 30 seconds
        }
      }

      return {
        file,
        fileName: file.name,
        fileSize: file.size,
        contentType: file.type,
        duration,
      };
    })
  );

  // Step 1: Get presigned URLs from API
  const { data: uploadData } = await apiClient.post<StoryUploadUrlResponse>(
    '/stories/upload-urls',
    {
      photos: filesWithMetadata.map((item) => ({
        fileName: item.fileName,
        fileSize: item.fileSize,
        contentType: item.contentType,
        duration: item.duration,
      })),
    }
  );

  const { storyId, uploads } = uploadData;

  // Step 2: Upload all files to S3 in parallel using presigned URLs
  await Promise.all(
    uploads.map((upload, index) => {
      const file = files[index];
      return axios.put(upload.uploadUrl, file, {
        headers: {
          'Content-Type': file.type,
        },
      });
    })
  );

  // Step 3: Confirm upload and create story metadata
  const mediaItems = uploads.map((upload, index) => ({
    photoId: upload.photoId,
    mediaType: upload.mediaType,
    duration: filesWithMetadata[index].duration,
    contentType: filesWithMetadata[index].contentType,
  }));

  const { data: story } = await apiClient.post<Story>('/stories', {
    storyId,
    mediaItems,
    caption: caption?.trim() || undefined,
  });

  return story;
};

/**
 * Delete story
 */
const deleteStory = async (storyId: string): Promise<void> => {
  await apiClient.delete(`/stories/${storyId}`);
};

/**
 * Like a story
 */
const likeStory = async (storyId: string): Promise<void> => {
  await apiClient.post(`/stories/${storyId}/likes`);
};

/**
 * Unlike a story
 */
const unlikeStory = async (storyId: string): Promise<void> => {
  await apiClient.delete(`/stories/${storyId}/likes`);
};

// ============================================================================
// TanStack Query Hooks
// ============================================================================

/**
 * Hook to fetch infinite story feed
 */
export const useStoryFeed = () => {
  const { isAuthenticated } = useAuth();

  return useInfiniteQuery({
    queryKey: ['stories', 'feed'],
    queryFn: ({ pageParam }) => fetchStoryFeed(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor,
    enabled: isAuthenticated,
  });
};

/**
 * Hook to fetch single story
 */
export const useStory = (storyId: string) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['stories', storyId],
    queryFn: () => fetchStory(storyId),
    enabled: !!storyId && isAuthenticated,
  });
};

/**
 * Hook to fetch user's stories
 */
export const useUserStories = (userId: string) => {
  const { isAuthenticated } = useAuth();

  return useQuery({
    queryKey: ['stories', 'user', userId],
    queryFn: () => fetchUserStories(userId),
    enabled: !!userId && isAuthenticated,
  });
};

/**
 * Hook to upload story (multiple photos)
 */
export const useUploadStory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ files, caption }: { files: File[]; caption?: string }) =>
      uploadStory(files, caption),
    onMutate: async ({ files, caption }) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['stories', 'feed'] });

      // Create optimistic story with local blob URLs for immediate display
      const optimisticStory: Story = {
        storyId: `temp-${Date.now()}`,
        userId: 'current-user',
        caption: caption?.trim(),
        uploadedAt: new Date().toISOString(),
        likeCount: 0,
        commentCount: 0,
        photoCount: files.length,
        photos: files.map((file, index) => {
          const isVideo = file.type === 'video/mp4';
          const blobUrl = URL.createObjectURL(file);

          return {
            photoId: `temp-photo-${index}`,
            storyId: `temp-${Date.now()}`,
            orderIndex: index,
            mediaType: isVideo ? ('video' as const) : ('image' as const),
            images: {
              thumbnail: blobUrl,
              medium: blobUrl,
              full: blobUrl,
            },
            videoUrl: isVideo ? blobUrl : undefined,
            contentType: file.type,
          };
        }),
        isLikedByCurrentUser: false,
      };

      // Get previous state for potential rollback
      const previousFeed = queryClient.getQueryData(['stories', 'feed']);

      // Optimistically add story to the top of the feed
      queryClient.setQueryData(['stories', 'feed'], (old: any) => {
        if (!old) {
          return {
            pages: [{ items: [optimisticStory], hasMore: false }],
            pageParams: [undefined],
          };
        }
        return {
          ...old,
          pages: old.pages.map((page: any, index: number) =>
            index === 0 ? { ...page, items: [optimisticStory, ...page.items] } : page
          ),
        };
      });

      return { previousFeed, optimisticStory };
    },
    onSuccess: (newStory, _vars, context) => {
      // Replace optimistic story with real story data
      queryClient.setQueryData(['stories', 'feed'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any, index: number) =>
            index === 0
              ? {
                  ...page,
                  items: page.items.map((story: any) =>
                    story.storyId === context?.optimisticStory.storyId ? newStory : story
                  ),
                }
              : page
          ),
        };
      });

      // Clean up blob URLs from optimistic update
      context?.optimisticStory.photos.forEach((photo) => {
        URL.revokeObjectURL(photo.images.thumbnail);
        URL.revokeObjectURL(photo.images.medium);
        URL.revokeObjectURL(photo.images.full);
      });

      // Invalidate to ensure we get the latest data
      queryClient.invalidateQueries({ queryKey: ['stories', 'feed'] });
    },
    onError: (_err, _vars, context) => {
      // Revert on error
      if (context?.previousFeed) {
        queryClient.setQueryData(['stories', 'feed'], context.previousFeed);
      }

      // Clean up blob URLs
      context?.optimisticStory.photos.forEach((photo) => {
        URL.revokeObjectURL(photo.images.thumbnail);
        URL.revokeObjectURL(photo.images.medium);
        URL.revokeObjectURL(photo.images.full);
      });
    },
  });
};

/**
 * Hook to delete story
 */
export const useDeleteStory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storyId: string) => deleteStory(storyId),
    onMutate: async (storyId) => {
      // Cancel ongoing queries
      await queryClient.cancelQueries({ queryKey: ['stories'] });

      // Save previous state for rollback
      const previousFeed = queryClient.getQueryData(['stories', 'feed']);
      const previousUserStories = queryClient.getQueriesData({ queryKey: ['stories', 'user'] });

      // Optimistically remove story from feed
      queryClient.setQueryData(['stories', 'feed'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.filter((story: any) => story.storyId !== storyId),
          })),
        };
      });

      // Optimistically remove story from user stories
      queryClient.setQueriesData({ queryKey: ['stories', 'user'] }, (old: any) => {
        if (!old) return old;
        return old.filter((story: any) => story.storyId !== storyId);
      });

      return { previousFeed, previousUserStories };
    },
    onError: (_err, _storyId, context) => {
      // Revert on error
      if (context?.previousFeed) {
        queryClient.setQueryData(['stories', 'feed'], context.previousFeed);
      }
      if (context?.previousUserStories) {
        context.previousUserStories.forEach(([queryKey, data]) => {
          queryClient.setQueryData(queryKey, data);
        });
      }
    },
    onSuccess: () => {
      // Invalidate to ensure consistency
      queryClient.invalidateQueries({ queryKey: ['stories'] });
    },
  });
};

/**
 * Hook to like a story
 */
export const useLikeStory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storyId: string) => likeStory(storyId),
    onMutate: async (storyId) => {
      // Optimistically update like status
      queryClient.setQueryData(['stories', 'feed'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((story: any) =>
              story.storyId === storyId
                ? {
                    ...story,
                    isLikedByCurrentUser: true,
                    likeCount: story.likeCount + 1,
                  }
                : story
            ),
          })),
        };
      });

      queryClient.setQueryData(['stories', storyId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          isLikedByCurrentUser: true,
          likeCount: old.likeCount + 1,
        };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stories'] });
    },
  });
};

/**
 * Hook to unlike a story
 */
export const useUnlikeStory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (storyId: string) => unlikeStory(storyId),
    onMutate: async (storyId) => {
      // Optimistically update like status
      queryClient.setQueryData(['stories', 'feed'], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          pages: old.pages.map((page: any) => ({
            ...page,
            items: page.items.map((story: any) =>
              story.storyId === storyId
                ? {
                    ...story,
                    isLikedByCurrentUser: false,
                    likeCount: Math.max(0, story.likeCount - 1),
                  }
                : story
            ),
          })),
        };
      });

      queryClient.setQueryData(['stories', storyId], (old: any) => {
        if (!old) return old;
        return {
          ...old,
          isLikedByCurrentUser: false,
          likeCount: Math.max(0, old.likeCount - 1),
        };
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['stories'] });
    },
  });
};
